import React, { FC, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { GetVisitTransactionsForAccountsDocument } from '../documents/GetVisitTransactionsForAccounts.generated';
import { appClient } from 'src/config/apollo';
import { useSelector } from 'react-redux';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

type ObjResponse = {
    closeInContext?: boolean;
    launchInContext?: boolean;
    returnURL?: string;
    redirectIframeUrl?: string;
};

export const paymentsIframeName = 'payments-iframe';

interface Props {
    sessionUuid: string;
    closeLoadingModal: () => void;
}

export const IncontextIframe: FC<Props> = ({ sessionUuid, closeLoadingModal }) => {
    const { uuid } = useSelector(getUserInformation);

    const getPrevDay = today => {
        return new Date(today.setDate(today.getDate() - 1));
    };

    const getTransaction = async () => {
        const yesterday = getPrevDay(new Date());
        const { data } = await appClient.query({
            query: GetVisitTransactionsForAccountsDocument,
            variables: { accountUuids: [uuid], startTime: yesterday, endTime: new Date() },
            fetchPolicy: 'network-only',
        });
        const transaction = data.getVisitTransactionsForAccounts.find(
            t => t.productUuid === sessionUuid,
        );
        return transaction;
    };

    const hideIframe = iframe => {
        iframe.style.visibility = 'hidden';
        iframe.style.opacity = '0';
        document.body.style.overflow = 'visible';
    };

    const onWindowMessage = useCallback(async message => {
        if (message.origin !== 'https://pay.instamed.com') {
            return false;
        }
        const iframe = document.getElementById('instamed');
        let obj = {} as ObjResponse;
        obj = JSON.parse(message.data);

        if (obj.closeInContext && iframe) {
            // gets called on cancel
            hideIframe(iframe);
        }
        if (obj.returnURL && iframe) {
            const transaction = await getTransaction();
            if (transaction && transaction.status === 'FULLY_PAID') {
                window.location.href = obj.returnURL;
            } else {
                closeLoadingModal();
                hideIframe(iframe);
            }
        }
        if (obj.launchInContext && iframe) {
            // starts the iframe flow
            document.body.style.overflow = 'hidden';
            iframe.style.visibility = 'visible';
            iframe.style.opacity = '1';
        }
        if (obj.redirectIframeUrl && iframe) {
            iframe.setAttribute('src', obj.redirectIframeUrl);
        }
        if (
            window.location.hash !== undefined &&
            window.location.hash !== null &&
            window.location.hash !== ''
        ) {
            window.location.hash = '';
        }
        return true;
    }, []);

    useEffect(() => {
        window.addEventListener('message', onWindowMessage);
        return () => {
            window.removeEventListener('message', onWindowMessage);
        };
    }, [onWindowMessage]);

    return <Iframe frameBorder={0} allowTransparency name={paymentsIframeName} id="instamed" />;
};

const Iframe = styled('iframe')({
    zIndex: 9999,
    display: 'block',
    backgroundColor: 'transparent',
    border: '0px none transparent',
    overflwX: 'hidden',
    overflowY: 'auto',
    visibility: 'hidden',
    margin: '0px',
    padding: '0px',
    WebkitTapHighlightColor: 'transparent',
    position: 'fixed',
    left: '0px',
    top: '0px',
    width: '100%',
    height: '100%',
});
